import React, { useState } from "react";
import { ShieldCheck, PartyPopper, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";

export default function FinalizePanel({ cid, checklistTitle, session, progress, canFinalize, onCompleted }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [handoff, setHandoff] = useState("");
  const overall = progress?.overall || { done: 0, total: 0 };
  const isFull = overall.total > 0 && overall.done === overall.total;
  const isDone = session?.status === "completed";
  const comps = session?.completions || {};
  const oorMissing = Object.values(comps).filter((m) => m && m.out_of_range && !(m.corrective || "").trim()).length;

  const submit = async () => {
    if (oorMissing > 0) {
      return toast.error(`${oorMissing} out-of-range reading(s) need a corrective action note in the Temp Log first.`);
    }
    if (!isFull && !note.trim()) {
      return toast.error("Not all items are checked. Add an override note to finalize.");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/checklists/${cid}/complete`, { override_note: note.trim() || null, handoff_note: handoff.trim() || null });
      toast.success(`Owner notified — ${checklistTitle} verified!`, { icon: "🎉", duration: 5000 });
      onCompleted?.(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="finalize-section" className="space-y-4">
      <div className="rounded-2xl border border-[#EF4444]/40 bg-[#EF4444]/[0.06] p-5">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-5 w-5 text-[#EF4444]" />
          <h3 className="font-heading text-lg font-semibold">Finalize {checklistTitle}</h3>
        </div>
        <p className="text-sm text-[#A3A3A3]">Confirm every item is complete, then submit to notify the owner.</p>
      </div>

      {isDone ? (
        <div className="rounded-2xl border border-[#16A34A]/40 bg-[#16A34A]/10 p-5 text-center">
          <PartyPopper className="h-7 w-7 text-[#16A34A] mx-auto mb-2" />
          <p className="font-heading font-semibold text-[#F5F5F5]">Verified & owner notified</p>
          <p className="text-sm text-[#A3A3A3] mt-1">Completed by {session.completed_by}</p>
        </div>
      ) : canFinalize ? (
        <div className={`rounded-2xl border p-5 ${isFull ? "border-[#EF4444]/50 gold-pulse" : "border-[#2E2E2E]"} bg-[#171717]`}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-xs uppercase tracking-widest text-[#6B6B6B]">Overall progress</span>
            <span className={`font-mono text-sm font-semibold ${isFull ? "text-[#16A34A]" : "text-[#E11D2A]"}`}>
              {overall.done}/{overall.total} items
            </span>
          </div>
          <div className="h-2 rounded-full bg-[#232323] overflow-hidden mb-4">
            <div className={`h-full transition-all duration-500 ${isFull ? "bg-[#16A34A]" : "bg-[#E11D2A]"}`} style={{ width: `${overall.total ? (overall.done / overall.total) * 100 : 0}%` }} />
          </div>
          {!isFull && (
            <textarea
              data-testid="override-note-input"
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Items remain — add an override note explaining why you're finalizing early…"
              rows={2}
              className="w-full mb-3 rounded-xl bg-[#0E0E0E] border border-[#2E2E2E] px-4 py-3 text-sm outline-none focus:border-[#E11D2A] transition-colors resize-none"
            />
          )}
          {oorMissing > 0 && (
            <div data-testid="finalize-corrective-warning" className="mb-3 flex items-start gap-2 rounded-xl border border-[#EF4444]/50 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#EF4444]">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{oorMissing} out-of-range reading{oorMissing > 1 ? "s" : ""} need a corrective action note in the Temp Log before you can finalize.</span>
            </div>
          )}
          <div className="mb-3">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5">Handoff note for owner <span className="text-[#4a4a4a]">(optional)</span></label>
            <textarea
              data-testid="handoff-note-input"
              value={handoff} onChange={(e) => setHandoff(e.target.value)}
              placeholder="Anything the owner should know tonight — e.g. low on fryer oil, walk-in running warm, register short…"
              rows={2}
              className="w-full rounded-xl bg-[#0E0E0E] border border-[#2E2E2E] px-4 py-3 text-sm outline-none focus:border-[#E11D2A] transition-colors resize-none"
            />
          </div>
          <button
            data-testid="supervisor-signoff-button"
            onClick={submit} disabled={busy || oorMissing > 0}
            className="w-full rounded-xl bg-[#E11D2A] text-white font-heading font-bold py-4 flex items-center justify-center gap-2 hover:bg-[#B91C1C] active:scale-[0.99] disabled:opacity-60 transition-all"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
            Submit & Notify Owner
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#171717] p-5 text-center text-sm text-[#6B6B6B]">
          Only the on-duty supervisor or the owner can submit the final verification.
        </div>
      )}
    </div>
  );
}
